import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import { useEffect } from 'react';
import { Bold, Italic, List, ListOrdered, Heading2 } from 'lucide-react';

/**
 * Éditeur de texte riche léger basé sur TipTap.
 * Props :
 *   value         : string HTML (contenu initial)
 *   onChange      : callback(html: string)
 *   placeholder   : string
 *   minHeight     : CSS string (default '120px')
 *   readOnly      : bool
 */
export default function RichTextEditor({ value, onChange, placeholder = 'Saisissez une description...', minHeight = '120px', readOnly = false }) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [2, 3] },
      }),
      Placeholder.configure({ placeholder }),
    ],
    content: value || '',
    editable: !readOnly,
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      // Retourne null si le contenu est vide (juste <p></p>)
      onChange(html === '<p></p>' ? '' : html);
    },
  });

  // Sync externe (si `value` change depuis l'extérieur sans interaction)
  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    const incoming = value || '';
    if (current !== incoming && incoming !== '<p></p>') {
      editor.commands.setContent(incoming, false);
    }
  }, [value, editor]);

  if (!editor) return null;

  const ToolBtn = ({ action, active, title, children }) => (
    <button
      type="button"
      onMouseDown={e => { e.preventDefault(); action(); }}
      title={title}
      style={{
        padding: '4px 7px',
        borderRadius: 6,
        border: 'none',
        background: active ? 'var(--primary-50, #EEF2FF)' : 'transparent',
        color: active ? 'var(--primary-solid, #4F46E5)' : 'var(--text-muted, #64748B)',
        cursor: 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        fontWeight: active ? 700 : 400,
        transition: 'background 0.15s',
      }}>
      {children}
    </button>
  );

  return (
    <div style={{ border: '1px solid var(--border, #E2E8F0)', borderRadius: 10, overflow: 'hidden', background: 'var(--bg, #fff)' }}>
      {/* Barre d'outils */}
      {!readOnly && (
        <div style={{
          display: 'flex', gap: 2, padding: '6px 8px',
          borderBottom: '1px solid var(--border, #E2E8F0)',
          background: 'var(--bg-subtle, #F8FAFC)',
          flexWrap: 'wrap',
        }}>
          <ToolBtn
            active={editor.isActive('bold')}
            action={() => editor.chain().focus().toggleBold().run()}
            title="Gras (Ctrl+B)">
            <Bold size={14} />
          </ToolBtn>
          <ToolBtn
            active={editor.isActive('italic')}
            action={() => editor.chain().focus().toggleItalic().run()}
            title="Italique (Ctrl+I)">
            <Italic size={14} />
          </ToolBtn>
          <span style={{ width: 1, background: 'var(--border, #E2E8F0)', margin: '2px 4px' }} />
          <ToolBtn
            active={editor.isActive('heading', { level: 2 })}
            action={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
            title="Titre">
            <Heading2 size={14} />
          </ToolBtn>
          <span style={{ width: 1, background: 'var(--border, #E2E8F0)', margin: '2px 4px' }} />
          <ToolBtn
            active={editor.isActive('bulletList')}
            action={() => editor.chain().focus().toggleBulletList().run()}
            title="Liste à puces">
            <List size={14} />
          </ToolBtn>
          <ToolBtn
            active={editor.isActive('orderedList')}
            action={() => editor.chain().focus().toggleOrderedList().run()}
            title="Liste numérotée">
            <ListOrdered size={14} />
          </ToolBtn>
        </div>
      )}
      {/* Zone de texte */}
      <EditorContent
        editor={editor}
        style={{ minHeight, padding: '10px 14px', fontSize: '0.875rem', lineHeight: 1.6, color: 'var(--text, #1E293B)', cursor: 'text' }}
      />
    </div>
  );
}
